import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "components/ui/dialog";
import { DialogCard } from "components/ui/dialog-card";
import { useCancelBooking } from "hooks/booking";
import { getServerMessage } from "lib/serverError";
import type { BookingInterface } from "types/booking";
import { Role } from "types/user";
import { isBookingOnSameDay } from "./format";

type CancelBookingDialogProps = {
    booking: BookingInterface;
    currentRole: Role.CLIENT | Role.PROVIDER;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const CancelBookingDialog: React.FC<CancelBookingDialogProps> = ({
    booking,
    currentRole,
    open,
    onOpenChange,
}) => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const cancelBooking = useCancelBooking();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLateCancellation = isBookingOnSameDay(booking);

    const handleConfirm = async () => {
        try {
            setIsSubmitting(true);
            await cancelBooking.mutateAsync(booking.id);
            await queryClient.invalidateQueries({ queryKey: ["profile", "service-history"] });
            toast.success(intl.formatMessage({ id: "profile.service_history.cancel_modal.success" }));
            onOpenChange(false);
        } catch (err: unknown) {
            toast.error(
                getServerMessage(err)
                    ?? intl.formatMessage({ id: "profile.service_history.cancel_modal.error" }),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const titleId = "profile.service_history.cancel_modal.title";
    const messageId = "profile.service_history.cancel_modal.message";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogCard closeLabel={intl.formatMessage({ id: "profile.service_history.cancel_modal.close" })}>
                <div className="flex flex-col gap-6">
                    <div>
                        <DialogTitle className="text-xl font-bold text-slate-900">
                            {intl.formatMessage({ id: titleId })}
                        </DialogTitle>
                        <DialogDescription className="mt-2 text-sm text-slate-500">
                            {intl.formatMessage({ id: messageId })}
                            {isLateCancellation && currentRole === Role.CLIENT
                                ? ` ${intl.formatMessage({ id: "profile.service_history.cancel_modal.late_warning" })}`
                                : null}
                        </DialogDescription>
                    </div>

                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => void handleConfirm()}
                        disabled={isSubmitting}
                        className="h-12 w-full rounded-xl text-base font-bold"
                    >
                        {intl.formatMessage({ id: "profile.service_history.cancel_modal.confirm" })}
                    </Button>
                </div>
            </DialogCard>
        </Dialog>
    );
};

export default CancelBookingDialog;
